/**
 * CityHubsIndex — /cities/hubs  (2026-02-29)
 *
 * Top-level directory of all industry & sector hubs. Sits above the
 * individual /cities/hubs/:tag ranked pages. Each card previews the
 * top-3 cities for its hub + the total count, then links into the
 * ranked page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEOHead from "@/components/app/SEOHead";
import { Trophy, ArrowRight, Sparkles, Briefcase } from "lucide-react";

export default function CityHubsIndex() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/cities/hubs");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sectors  = (data?.hubs || []).filter((h) => h.kind === "sector");
  const industries = (data?.hubs || []).filter((h) => h.kind === "industry");

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <SEOHead
        title="Industry hubs — top student cities by sector | WhichUniversityAdvisor"
        description="Ranked lists of the top student cities for each industry: law, finance, tech, biotech, aerospace, fintech and more. 18 hubs, 300+ cities."
      />

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-6">
        <Link to="/cities" data-testid="hubs-back-link"
              className="text-xs uppercase tracking-wider text-stone-500 hover:text-[#002147] inline-flex items-center gap-1.5">
          ← Cities
        </Link>
        <div className="mt-4 flex items-center gap-3">
          <Trophy className="h-6 w-6 text-[#7C3AED]" />
          <span className="text-xs uppercase tracking-wider text-[#7C3AED] font-semibold">
            Industry & sector hubs
          </span>
        </div>
        <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tight text-[#002147]">
          Which cities lead <span className="text-[#7C3AED]">each industry?</span>
        </h1>
        <p className="mt-4 text-stone-600 max-w-2xl text-base">
          18 ranked lists — 9 broad sectors and 9 specific industries — built from Startup Genome, GFCI, Magic Circle firm presence, aerospace manufacturing footprints, biotech clinical trials and more. Every rank comes with visible evidence.
        </p>
      </section>

      {err && (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{err}</div>
        </div>
      )}

      {data && (
        <>
          <HubGrid
            title="Broad sectors"
            subtitle="Best-fit at a glance — for students weighing city-shape decisions"
            hubs={sectors}
            testId="hubs-sectors"
            Icon={Sparkles}
          />
          <HubGrid
            title="Specific industries"
            subtitle="Fine-grained hubs — for students set on a particular career path"
            hubs={industries}
            testId="hubs-industries"
            Icon={Briefcase}
          />

          {/* Method footer */}
          <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16">
            <div className="bg-white/70 border border-stone-200 rounded-lg p-5 text-xs text-stone-600 leading-relaxed" data-testid="hubs-method">
              <div className="font-semibold text-stone-700 mb-1">Methodology</div>
              <p>
                Each hub aggregates a specific 0–10 signal per city (Startup Genome for startups, GFCI for finance, Magic Circle presence for law, aerospace manufacturing footprints, biotech clinical-trial density, etc.). A city carries a hub tag when it scores ≥ 7 on that signal. Data sourced from university-cities research shortlist (Feb 2026), 1,000-city dataset.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function HubGrid({ title, subtitle, hubs, testId, Icon }) {
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-8" data-testid={testId}>
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-[#7C3AED]" /> {title}
        </div>
        <p className="text-sm text-stone-600 mt-1">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {hubs.map((h) => (
          <Link
            key={h.tag}
            to={`/cities/hubs/${h.tag}`}
            data-testid={`hubs-card-${h.tag}`}
            className="group bg-white border border-stone-200 rounded-xl p-5 hover:border-[#7C3AED] hover:shadow-md transition"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-editorial text-xl leading-tight text-[#002147] group-hover:text-[#7C3AED]">
                  {h.label}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">{h.blurb}</div>
              </div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-800 whitespace-nowrap">
                {h.count} cities
              </span>
            </div>

            {h.top_cities.length > 0 && (
              <div className="mt-4 space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-stone-400">Top 3</div>
                {h.top_cities.map((c, i) => (
                  <div key={c.slug} className="flex items-baseline justify-between text-[13px] text-stone-700">
                    <span>
                      <span className="text-stone-400 font-mono mr-2">#{i + 1}</span>
                      {c.name}
                    </span>
                    <span className="font-mono text-[11px] text-[#7C3AED]">
                      {c.law_firms_present != null
                        ? `${c.law_firms_present}/13 firms`
                        : c.anchor_firms_present != null
                        ? `${c.anchor_firms_present}/${c.anchor_panel_size} ${h.tag === "finance" ? "G-SIBs" : "anchors"}`
                        : `${c.sector_score ?? c.raw_max}/10`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 pt-3 border-t border-stone-100 text-[12px] text-[#7C3AED] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              See ranked list <ArrowRight className="h-3 w-3" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
